import { AnimatePresence, Reorder } from 'framer-motion';
import { Star, Trash2, MapPin, GripVertical } from 'lucide-react';
import { useState, useEffect, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/authStore';
import { API_BASE_URL } from '@/config/api';
import { useFavoritesStore } from '../../stores/favoritesStore';

export interface FavoriteLocation {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  order: number;
  createdAt: string;
}

interface FavoritesListProps {
  onSelectLocation: (lat: number, lon: number) => void;
  currentLocation?: { lat: number; lon: number };
  refreshTrigger?: number;
  ref?: React.Ref<FavoritesListHandle>;
}

export interface FavoritesListHandle {
  refreshFavorites: () => Promise<void>;
}

const FavoritesList = ({ onSelectLocation, currentLocation, refreshTrigger, ref }: FavoritesListProps) => {
  const { t } = useTranslation();
  const { isAuthenticated, token } = useAuthStore();
  const localFavorites = useFavoritesStore((state) => state.favorites);
  const removeLocalFavorite = useFavoritesStore((state) => state.removeFavorite);
  const [favorites, setFavorites] = useState<FavoriteLocation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch favorites from backend
  const fetchFavorites = async () => {
    if (!isAuthenticated || !token) {
      setFavorites(localFavorites.map((favorite, index) => ({
        ...favorite,
        order: index,
        createdAt: '',
      })));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/favorites`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(t('errors.loadFavoritesError'));
      }

      const data = await response.json();
      const items = Array.isArray(data) ? data : data.favorites || [];

      const normalized = items.map((fav: any) => ({
        id: fav.id,
        name: fav.name,
        latitude: fav.latitude,
        longitude: fav.longitude,
        order: fav.order ?? fav.order,
        createdAt: fav.createdAt ?? fav.created_at ?? '',
      }));

      const sorted = [...normalized].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setFavorites(sorted);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten');
    } finally {
      setIsLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    refreshFavorites: fetchFavorites,
  }));

  useEffect(() => {
    fetchFavorites();
  }, [isAuthenticated, token, refreshTrigger, localFavorites]);

  // Remove favorite
  const removeFavorite = async (id: string) => {
    if (!token) {
      removeLocalFavorite(id);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/favorites/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(t('errors.deleteFavoriteError'));
      }

      setFavorites((prev) => prev.filter((fav) => fav.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten');
    }
  };

  // Reorder favorites
  const handleReorder = async (newOrder: FavoriteLocation[]) => {
    if (!token) return;

    setFavorites(newOrder);

    try {
      const favoriteIds = newOrder.map((fav) => fav.id);
      const response = await fetch(`${API_BASE_URL}/favorites/reorder`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ favorite_ids: favoriteIds }),
      });

      if (!response.ok) {
        throw new Error(t('errors.sortFavoritesError'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten');
      // Revert on error
      fetchFavorites();
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="text-center py-8 text-text-secondary">
        <Star className="w-12 h-12 mx-auto mb-2 opacity-50" />
        <p>Melden Sie sich an, um Favoriten zu speichern</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent mx-auto" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-danger">
        <p>{error}</p>
        <button
          onClick={fetchFavorites}
          className="mt-2 text-sm underline"
        >
          Erneut versuchen
        </button>
      </div>
    );
  }

  if (favorites.length === 0) {
    return (
      <div className="text-center py-8 text-text-secondary">
        <Star className="w-12 h-12 mx-auto mb-2 opacity-50" />
        <p>{t('ui.noFavorites')}</p>
        <p className="text-sm mt-1">{t('ui.addFavoritesHint')}</p>
      </div>
    );
  }

  const isCurrentLocation = (fav: FavoriteLocation) =>
    currentLocation &&
    Math.abs(currentLocation.lat - fav.latitude) < 0.01 &&
    Math.abs(currentLocation.lon - fav.longitude) < 0.01;

  return (
    <div className="space-y-2">
      <Reorder.Group axis="y" values={favorites} onReorder={handleReorder}>
        <AnimatePresence>
          {favorites.map((fav) => {
            const isCurrent = isCurrentLocation(fav);
            return (
              <Reorder.Item
                key={fav.id}
                value={fav}
                initial={false}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ layout: { duration: 0.2 } }}
                className={`
                  flex items-center justify-between p-4 rounded-lg
                  ${
                    isCurrent
                      ? 'bg-primary/10 border-2 border-primary'
                      : 'bg-surface hover:bg-surface-elevated'
                  }
                  transition-colors group
                `}
              >
                <div className="flex items-center gap-3 cursor-grab active:cursor-grabbing">
                  <GripVertical className="w-5 h-5 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <div className="flex items-center gap-3 flex-1 cursor-pointer" onClick={() => onSelectLocation(fav.latitude, fav.longitude)}>
                  {isCurrent ? (
                    <MapPin className="w-5 h-5 text-primary" />
                  ) : (
                    <Star className="w-5 h-5 text-accent" />
                  )}
                  <div>
                    <h3 className="font-semibold text-text-primary">{fav.name}</h3>
                    <p className="text-sm text-text-secondary">
                      {fav.latitude.toFixed(2)}°, {fav.longitude.toFixed(2)}°
                    </p>
                  </div>
                </div>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFavorite(fav.id);
                  }}
                  className="opacity-0 group-hover:opacity-100 p-2 rounded-lg hover:bg-danger/10 text-danger transition-all"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </Reorder.Item>
            );
          })}
        </AnimatePresence>
      </Reorder.Group>
    </div>
  );
};

FavoritesList.displayName = 'FavoritesList';

export default FavoritesList;
